/**
 * Industry Selector Component
 * Dropdown for industry selection with custom downtime input
 */

import React from 'react'
import { IndustryType } from '../types/calculator.types'
import { getIndustryOptions, getIndustryDefaults } from '../utils/industryDefaults'
import { findFreightCity } from '../utils/freightCities'
import styles from './IndustrySelector.module.css'

interface IndustrySelectorProps {
  industry: IndustryType
  customDowntimeCost?: number
  downtimeHours: number
  deliveryCity?: string
  onIndustryChange: (industry: IndustryType) => void
  onCustomDowntimeCostChange: (cost: number) => void
  onDowntimeHoursChange: (hours: number) => void
  errors?: {
    industry?: string
    customDowntimeCost?: string
    downtimeHours?: string
  }
  disabled?: boolean
  className?: string
}

const IndustrySelector: React.FC<IndustrySelectorProps> = ({
  industry,
  customDowntimeCost = 0,
  downtimeHours,
  deliveryCity = '',
  onIndustryChange,
  onCustomDowntimeCostChange,
  onDowntimeHoursChange,
  errors = {},
  disabled = false,
  className = ''
}) => {
  const industryOptions = getIndustryOptions()
  const selectedIndustryDefaults = getIndustryDefaults(industry)
  const showCustomDowntimeInput = industry === 'custom'

  // Determine currency based on delivery city
  const getCurrencyForCity = (cityName: string): 'CAD' | 'USD' => {
    if (!cityName) return 'CAD' // Default to CAD
    
    // Try to find exact match first
    let freightCity = findFreightCity(cityName)
    
    // If no exact match, try different variations
    if (!freightCity) {
      const cleanCityName = cityName.toLowerCase().trim()
      
      // Try just the first word (city name)
      const firstWord = cleanCityName.split(/[\s,()]+/)[0]
      freightCity = findFreightCity(firstWord)
      
      // If still no match, check if the string contains known US indicators
      if (!freightCity) {
        const usIndicators = ['usa', 'united states', 'us', 'texas', 'california', 'new york', 'florida']
        const hasUSIndicator = usIndicators.some(indicator => 
          cleanCityName.includes(indicator.toLowerCase())
        )
        
        if (hasUSIndicator) {
          return 'USD'
        }
      }
    }
    
    if (freightCity && freightCity.country === 'USA') {
      return 'USD'
    }
    return 'CAD' // Default to CAD for Canada and other countries
  }

  const currency = getCurrencyForCity(deliveryCity)

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  return (
    <div className={`${styles.container} ${className}`}>
      <h3 className={styles.sectionTitle}>Industry & Downtime</h3>

      {/* Industry Information */}
      <div className={styles.industryDetails}>
        <span className={styles.helpText}>
          Industry choice sets downtime cost per hour and resurfacing interval for calculations.
        </span>
        <ul className={styles.industryList}>
          <li>
            <strong>Manufacturing:</strong> Downtime ~$260,000/hr, resurfacing every 5–7 years
            <span className={styles.source}>(Forbes, Sumitomo Drive, Gitnux; $200k–$320k/hr)</span>
          </li>
          <li>
            <strong>Automotive:</strong> Downtime ~$2.3–3M/hr, resurfacing every 4–5 years
            <span className={styles.source}>(Siemens, IndustryWeek, Forbes; $2.3M–$3M/hr)</span>
          </li>
          <li>
            <strong>Data Center:</strong> Downtime ~$600,000/hr, resurfacing every 8 years
            <span className={styles.source}>(Uptime Institute, Pingdom, Ponemon; $500k–$700k/hr)</span>
          </li>
          <li>
            <strong>Hydroelectric:</strong> Downtime ~$20,000/hr, resurfacing every 5–10 years
            <span className={styles.source}>(U.S. DOE, Industry Case Studies; $15k–$25k/hr)</span>
          </li>
          <li>
            <strong>Warehouse:</strong> Downtime ~$75,000/hr, resurfacing every 7–10 years
            <span className={styles.source}>(Logistics Management, Supply Chain Dive; $50k–$100k/hr)</span>
          </li>
          <li>
            <strong>Distribution Center:</strong> Downtime ~$225,000/hr, resurfacing every 6–8 years
            <span className={styles.source}>(Material Handling & Logistics; $150k–$300k/hr)</span>
          </li>
          <li>
            <strong>Transportation Hub:</strong> Downtime ~$140,000/hr, resurfacing every 7–10 years
            <span className={styles.source}>(Airport Council International, Rail Industry; $80k–$200k/hr)</span>
          </li>
          <li>
            <strong>Utilities:</strong> Downtime ~$55,000/hr, resurfacing every 10–15 years
            <span className={styles.source}>(Energy Industry Reports; $30k–$80k/hr)</span>
          </li>
          <li>
            <strong>Retail:</strong> Downtime ~$12,500/hr, resurfacing every 5–7 years
            <span className={styles.source}>(Retail Industry Analysis; $5k–$20k/hr)</span>
          </li>
          <li>
            <strong>Mining:</strong> Downtime ~$400,000/hr, resurfacing every 3–5 years
            <span className={styles.source}>(Mining Ops Studies, Heavy Industry Benchmarks; $300k–$500k/hr)</span>
          </li>
          <li>
            <strong>Custom:</strong> Enter your own downtime value and resurfacing schedule
            <span className={styles.source}>(User-defined parameters for specialized facilities)</span>
          </li>
        </ul>
        <span className={styles.helpText}>
          Industry selection directly impacts downtime loss, ROI, and savings results.
        </span>
        <span className={styles.helpText}>
          Resurfacing cycles per Industry Guidelines, National Ready Mixed Concrete Association.
        </span>
      </div>

      {/* Industry Selection */}
      <div className={styles.inputGroup}>
        <label htmlFor="industry" className={styles.label}>
          Industry Type
          <span className={styles.required}>*</span>
        </label>
        <select
          id="industry"
          value={industry}
          onChange={(e) => onIndustryChange(e.target.value as IndustryType)}
          disabled={disabled}
          className={`${styles.select} ${errors.industry ? styles.inputError : ''}`}
          aria-describedby={errors.industry ? 'industry-error' : undefined}
        >
          {industryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.industry && (
          <span id="industry-error" className={styles.errorText} role="alert">
            {errors.industry}
          </span>
        )}

        {/* Industry Info Display */}
        {selectedIndustryDefaults && (
          <div className={styles.industryInfo}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Downtime Cost:</span>
              <span className={styles.infoValue}>
                {formatCurrency(selectedIndustryDefaults.downtimeCostPerHour)}/hour
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Resurfacing Interval:</span>
              <span className={styles.infoValue}>
                {selectedIndustryDefaults.resurfacingIntervalYears.standard === selectedIndustryDefaults.resurfacingIntervalYears.thick
                  ? `${selectedIndustryDefaults.resurfacingIntervalYears.standard} years`
                  : `${selectedIndustryDefaults.resurfacingIntervalYears.standard}-${selectedIndustryDefaults.resurfacingIntervalYears.thick} years`}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Custom Downtime Cost Input */}
      {showCustomDowntimeInput && (
        <div className={styles.inputGroup}>
          <label htmlFor="customDowntimeCost" className={styles.label}>
            Custom Downtime Cost ({currency}/hour)
            <span className={styles.required}>*</span>
          </label>
          <span className={styles.helpText}>
            Set your own hourly downtime cost if "Custom" is selected.
          </span>
          <div className={styles.currencyInputWrapper}>
            <span className={styles.currencySymbol}>{currency}</span>
            <input
              id="customDowntimeCost"
              type="number"
              value={customDowntimeCost || ''}
              onChange={(e) => onCustomDowntimeCostChange(Number(e.target.value))}
              placeholder="0"
              min="0"
              max="10000000"
              step="1000"
              disabled={disabled}
              className={`${styles.input} ${styles.currencyInput} ${errors.customDowntimeCost ? styles.inputError : ''}`}
              aria-describedby={errors.customDowntimeCost ? 'customDowntimeCost-error' : undefined}
            />
          </div>
          {errors.customDowntimeCost && (
            <span id="customDowntimeCost-error" className={styles.errorText} role="alert">
              {errors.customDowntimeCost}
            </span>
          )}
        </div>
      )}

      {/* Downtime Hours Input */}
      <div className={styles.inputGroup}>
        <label htmlFor="downtimeHours" className={styles.label}>
          Expected Downtime (hours per resurfacing)
          <span className={styles.required}>*</span>
        </label>
        
        {/* Quick Presets for Common Downtime Hours */}
        <div className={styles.quickPresets}>
          <span className={styles.presetsLabel}>Quick presets:</span>
          <div className={styles.presetButtons}>
            {[24, 48, 72, 168].map((hours) => (
              <button
                key={hours}
                type="button"
                onClick={() => onDowntimeHoursChange(hours)}
                disabled={disabled || downtimeHours === hours}
                className={`btn-preset ${downtimeHours === hours ? styles.active : ''}`}
              >
                {hours}h
                {hours >= 168 && <span className={styles.presetSubtext}>({Math.round(hours / 24)}d)</span>}
              </button>
            ))}
          </div>
        </div>

        <input
          id="downtimeHours"
          type="number"
          value={downtimeHours || ''}
          onChange={(e) => onDowntimeHoursChange(Number(e.target.value))}
          placeholder="Enter hours of downtime"
          min="0"
          max="8760"
          step="1"
          disabled={disabled}
          className={`${styles.input} ${errors.downtimeHours ? styles.inputError : ''}`}
          aria-describedby={errors.downtimeHours ? 'downtimeHours-error' : undefined}
        />
        {errors.downtimeHours && (
          <span id="downtimeHours-error" className={styles.errorText} role="alert">
            {errors.downtimeHours}
          </span>
        )}
        <span className={styles.helpText}>
          Hours of facility downtime required for each resurfacing event
        </span>
      </div>
    </div>
  )
}

export default IndustrySelector